//
// Copyright (c) 2011-2023 by Curalate, Inc.
//

import { TikTokConnectionStatus } from '@bvengineering/tiktok-gateway-service-types';
import { PayloadAction, createSlice } from '@reduxjs/toolkit';

import {
  ITikTokSettingsState,
  TIKTOK_SETTINGS_URL,
  getTikTokSettingsUrlWithError,
} from '@account-settings/redux/tiktok/tiktokSettingsModel';
import * as tiktokApi from '@apis/tiktok-settings/tiktokSettingsApi';
import { toastNotificationsActions } from '@legacy-components/common/toast-notifications/toastNotificationsActions';
import { ToastType } from '@legacy-components/common/toast-notifications/toastNotificationsModel';
import { SliceNames } from '@redux/SliceNames';
import { createThunk } from '@redux/createThunk';

const initialState: ITikTokSettingsState = {
  errors: {
    fetchOauthUrlError: undefined,
    fetchSettingsError: undefined,
    connectError: undefined,
    disconnectError: undefined,
    reconnectError: undefined,
  },
  flags: {
    isFetchingOAuthUrl: false,
    isFetchingSettings: false,
    isConnecting: false,
    isDisconnecting: false,
    isReconnecting: false,
  },
  oauthEndpoint: undefined,
  isConnected: false,
  hasStaleCredentials: false,
  connectedAccount: undefined,
};

const { actions, reducer: TikTokSettingsReducer } = createSlice({
  name: SliceNames.AccountSettingsTikTok,
  initialState,
  reducers: {
    requestTikTokSettings(state) {
      state.flags.isFetchingSettings = true;
      state.errors.fetchSettingsError = undefined;
    },
    recieveTikTokSettingsSucceeded(
      state,
      action: PayloadAction<TikTokConnectionStatus>,
    ) {
      state.isConnected = action.payload.isConnected;
      state.hasStaleCredentials = action.payload.hasStaleCredentials;
      state.connectedAccount = {
        displayName: action.payload.displayName,
        scope: action.payload.scope,
      };
      state.flags.isFetchingSettings = false;
    },
    recieveTikTokSettingsFailed(state) {
      state.flags.isFetchingSettings = false;
    },
    requestTikTokOAuthEndpoint(state) {
      state.flags.isFetchingOAuthUrl = true;
      state.errors.fetchOauthUrlError = undefined;
    },
    receiveTikTokOAuthEndpointSucceeded(state, action: PayloadAction<string>) {
      state.oauthEndpoint = action.payload;
      state.flags.isFetchingOAuthUrl = false;
    },
    receiveTikTokOAuthEndpointFailed(state) {
      state.flags.isFetchingOAuthUrl = false;
    },
    requestConnectTikTok(state) {
      state.flags.isConnecting = true;
      state.errors.connectError = undefined;
    },
    recieveConnectTikTokSucceeded(
      state,
      action: PayloadAction<TikTokConnectionStatus>,
    ) {
      state.isConnected = action.payload.isConnected;
      state.hasStaleCredentials = action.payload.hasStaleCredentials;
      state.connectedAccount = {
        displayName: action.payload.displayName,
        scope: action.payload.scope,
      };
      state.flags.isConnecting = false;
    },
    recieveConnectTikTokFailed(state) {
      state.flags.isConnecting = false;
    },
    requestDeAuthTikTok(state) {
      state.flags.isDisconnecting = true;
      state.errors.disconnectError = undefined;
    },
    recieveDeAuthTikTokSucceeded(state) {
      state.isConnected = false;
      state.connectedAccount = undefined;
      state.hasStaleCredentials = false;
      state.flags.isDisconnecting = false;
    },
    recieveDeAuthTikTokFailed(state) {
      state.flags.isDisconnecting = false;
    },
    requestReconnectTikTok(state) {
      state.flags.isReconnecting = true;
      state.errors.reconnectError = undefined;
    },
    recieveReconnectTikTokSucceeded(state, action: PayloadAction<string>) {
      state.flags.isReconnecting = false;
      state.oauthEndpoint = action.payload;
    },
    recieveReconnectTikTokFailed(state) {
      state.flags.isReconnecting = false;
    },
  },
});

export const getTikTokSettings = createThunk(() => async (dispatch) => {
  dispatch(actions.requestTikTokSettings());

  try {
    const response = await tiktokApi.getConnectionStatus();
    dispatch(actions.recieveTikTokSettingsSucceeded(response));
  } catch (_e) {
    dispatch(actions.recieveTikTokSettingsFailed());
  }
});

export const getOauthUrl = createThunk(() => async (dispatch) => {
  dispatch(actions.requestTikTokOAuthEndpoint());

  try {
    const url = await tiktokApi.getOAuthUrl();
    dispatch(actions.receiveTikTokOAuthEndpointSucceeded(url));
  } catch (_e) {
    dispatch(actions.receiveTikTokOAuthEndpointFailed());
  }
});

export const connectTikTok = createThunk(
  (state: string, authCode: string) => async (dispatch) => {
    dispatch(actions.requestConnectTikTok());

    try {
      const connectionStatus = await tiktokApi.connect({ state, authCode });
      dispatch(actions.recieveConnectTikTokSucceeded(connectionStatus));
      // Redirect to settings after connected
      window.location.replace(TIKTOK_SETTINGS_URL);
    } catch (_e) {
      dispatch(actions.recieveConnectTikTokFailed());

      // If an error occurs we redirect to settings with an error code query param
      window.location.replace(getTikTokSettingsUrlWithError('0'));
    }
  },
);

export const disconnectTikTok = createThunk(() => async (dispatch) => {
  dispatch(actions.requestDeAuthTikTok());

  try {
    await tiktokApi.revokeToken();
    dispatch(actions.recieveDeAuthTikTokSucceeded());

    dispatch(
      toastNotificationsActions.addToastNotification({
        title: 'TikTok account successfully disconnected!',
        type: ToastType.SUCCESS,
      }),
    );
  } catch (_e) {
    dispatch(actions.recieveDeAuthTikTokFailed());
    dispatch(
      toastNotificationsActions.addToastNotification({
        title:
          'An error occurred when trying to disconnect your TikTok account.',
        type: ToastType.ERROR,
      }),
    );
  }
});

export const reconnectTikTok = createThunk(() => async (dispatch) => {
  dispatch(actions.requestReconnectTikTok());

  try {
    await tiktokApi.revokeToken();
    const url = await tiktokApi.getOAuthUrl();

    dispatch(actions.recieveReconnectTikTokSucceeded(url));
  } catch (e) {
    dispatch(actions.recieveReconnectTikTokFailed());

    dispatch(
      toastNotificationsActions.addToastNotification({
        title:
          'An error occurred when trying to reconnect your TikTok account.',
        type: ToastType.ERROR,
      }),
    );
  }
});

export default TikTokSettingsReducer;
